import * as yup from "yup";
import { emailRegEx } from "./RegEx";

export const getCheckoutSchema = (checkoutData?: ICheckoutForm) => {
  let schema = yup.object().shape({
    // Shipping information
    firstName: yup
      .string()
      .min(3, "Must be at least 3 char")
      .max(50, "Must be at most 50 char")
      .required("First name is required"),
    lastName: yup
      .string()
      .min(3, "Must be at least 3 char")
      .max(50, "Must be at most 50 char")
      .required("Last name is required"),
    email: yup
      .string()
      .matches(emailRegEx, "Should be valid email")
      .required("Email is required"),
    streetAddress: yup
      .string()
      .min(3, "Must be at least 3 char")
      .max(50, "Must be at most 50 char")
      .required("Address is required"),
    city: yup
      .string()
      .min(3, "Must be at least 3 char")
      .max(50, "Must be at most 50 char")
      .required("City is required"),
    zipCode: yup
      .string()
      .trim()
      .min(3, "Zip code must be at least 3 characters long.")
      .max(10, "Zip code must be no more than 10 characters long.")
      .required("ZIP code is required"),
    idCountry: yup.number().required("Country is required"),
    idState: yup
      .number()
      .min(1, "State is required.")
      .required("State is required"),
    phoneNumber: yup
      .string()
      .trim()
      .min(12, "Must be at least 12 char")
      .max(17, "Must be at most 17 char")
      .required("Phone is required"),
    shippingMethod: yup
      .string()
      .min(1, "Shipping method is required.")
      .required("Shipping method is required"),

    // Billing information
    billingAddress: yup.boolean(),
    billingFirstName: yup.string().when("billingAddress", {
      is: false,
      then: (schema) =>
        schema
          .min(3, "Must be at least 3 char")
          .max(50, "Must be at most 50 char")
          .required("First name is required"),
      otherwise: (schema) => schema.optional(),
    }),
    billingLastName: yup.string().when("billingAddress", {
      is: false,
      then: (schema) =>
        schema
          .min(3, "Must be at least 3 char")
          .max(50, "Must be at most 50 char")
          .required("Last name is required"),
      otherwise: (schema) => schema.optional(),
    }),
    billingStreetAddress: yup.string().when("billingAddress", {
      is: false,
      then: (schema) =>
        schema
          .min(3, "Must be at least 3 char")
          .max(50, "Must be at most 50 char")
          .required("Address is required"),
      otherwise: (schema) => schema.optional(),
    }),
    billingCity: yup.string().when("billingAddress", {
      is: false,
      then: (schema) =>
        schema
          .min(3, "Must be at least 3 char")
          .max(50, "Must be at most 50 char")
          .required("City is required"),
      otherwise: (schema) => schema.optional(),
    }),
    billingZipCode: yup.string().when("billingAddress", {
      is: false,
      then: (schema) =>
        schema
          .trim()
          .min(3, "Zip code must be at least 3 characters long.")
          .max(10, "Zip code must be no more than 10 characters long.")
          .required("ZIP code is required"),
      otherwise: (schema) => schema.optional(),
    }),
    billingIdCountry: yup.number().when("billingAddress", {
      is: false,
      then: (schema) => schema.required("Country is required"),
      otherwise: (schema) => schema.optional(),
    }),
    billingIdState: yup.number().when("billingAddress", {
      is: false,
      then: (schema) =>
        schema.min(1, "State is required.").required("State is required"),
      otherwise: (schema) => schema.optional(),
    }),
    billingEmail: yup.string().when("billingAddress", {
      is: false,
      then: (schema) =>
        schema
          .matches(emailRegEx, "Should be valid email")
          .required("Email is required"),
      otherwise: (schema) => schema.optional(),
    }),
    billingPhoneNumber: yup.string().when("billingAddress", {
      is: false,
      then: (schema) =>
        schema
          .trim()
          .min(12, "Must be at least 12 char")
          .max(17, "Must be at most 17 char")
          .required("Phone is required"),
      otherwise: (schema) => schema.optional(),
    }),

    // Payment information
    number: yup.string().when("paymentMethodId", {
      is: (val: number) => !val,
      then: (schema) => schema.required("Card number is required"),
      otherwise: (schema) => schema.optional(),
    }),
    expiry: yup.string().when("paymentMethodId", {
      is: (val: number) => !val,
      then: (schema) => schema.required("Expiry date is required"),
      otherwise: (schema) => schema.optional(),
    }),
    cvc: yup.string().when("paymentMethodId", {
      is: (val: number) => !val,
      then: (schema) => schema.required("CVC is required"),
      otherwise: (schema) => schema.optional(),
    }),
    cardholder: yup.string().when("paymentMethodId", {
      is: (val: number) => !val,
      then: (schema) => schema.required("Cardholder name is required"),
      otherwise: (schema) => schema.optional(),
    }),
  });

  if (checkoutData?.paymentMethods?.length > 0) {
    schema = schema.concat(
      yup.object().shape({
        paymentMethodId: yup
          .number()
          .min(1, "Payment method is required.")
          .required("Payment method is required"),
      })
    );
  } else {
    schema = schema.concat(
      yup.object().shape({
        number: yup
          .string()
          .required("Card number is required")
          .transform((value) => value.replace(/\s+/g, ""))
          .matches(/^\d+$/, "Card number must contain only digits")
          .min(13, "Card number must be at least 13 digits")
          .max(16, "Card number must be at most 16 digits"),
        expiry: yup.string().required("Card expiry is required"),
        cvc: yup.string().required("CVC is required"),
        cardholder: yup.string().required("Cardholder is required"),
        billingAddress: yup.boolean(),
      })
    );
  }

  return schema;
};
